/**
 * @changelog
 *
 * 2026-03-11 (0.1.0) (jhoward)
 *   - Add App\Helpers\getAccountOffers() to fetch and cache offers, update user state, and handle loading UI.
 *   - Logs cache status and manages loader/overlay state.
 *
 * 2026-03-13 (0.1.1) (jhoward)
 *   - Show snack notification if response.message is present after fetching offers.
 *
 * 2026-04-09 (0.1.2) (jhoward)
 *   - Add new_pin_required and account_reverify_required to OffersServerResponse.
 *   - Update appStore state with new_pin_required.
 *
 * 2026-10-10 (0.2.0) (jhoward)
 *   - Add isSilent, for a revalidation of offers the patron is already looking at: it leaves the loading overlay and its
 *     "Loading offers..." text alone and asks the proxy not to show the spinner, so a refresh does not flash the page.
 *
 * 2026-10-15 (0.2.1) (jhoward)
 *   - Reset isFetchOffersInProgress and the loader in a finally. A fetch that threw (network failure, a non-JSON 502)
 *     left the flag set, so Offers stayed loading and no later call fetched again until a reload.
 */

import type { FetchResponse } from '@framework/defs/framework';
import type { OpcOffer } from '@/defs/api/OpcOffer';
import type { User } from '@/defs/user';
import peppermillCustomFetchProxy from '@/helpers/peppermillCustomFetchProxy';
import useAppStore from '@/store';
import { offers } from '@rss/routes/account';

interface OffersServerResponse extends FetchResponse {
  data?: {
    user: User;
    offers: OpcOffer[];
    offers_from_cache: boolean;
    new_pin_required: boolean;
    account_reverify_required: boolean;
  };
}

export default async function getAccountOffers(isCacheIgnored: boolean, isSilent = false): Promise<OpcOffer[]> {
  const appStore = useAppStore();
  if (!appStore.state.user) {
    return [];
  }

  if (appStore.account.offers && !isCacheIgnored) {
    return appStore.account.offers;
  }

  if (!appStore.isFetchOffersInProgress) {
    appStore.isFetchOffersInProgress = true;
    if (!isSilent) {
      appStore.isLoaderActive = true;
      appStore.overlayText = 'Loading offers...';
    }

    try {
      const response = await peppermillCustomFetchProxy<OffersServerResponse>(
        { ignoreCache: isCacheIgnored ? 1 : 0 },
        {
          route: offers(),
          showLoadingSpinner: !isSilent,
        },
      );

      if (response.message?.length) {
        appStore.setNewSnack(10000, response.message, response.status === 200 ? 'check' : 'error');
      }

      if (response.data?.offers) {
        appStore.account.offers = response.data.offers;
      }

      if (response.data?.user) {
        appStore.state.user = response.data.user;
      }

      appStore.state.new_pin_required = response.data?.new_pin_required === true;
    } catch (error) {
      console.error(error);
      if (!isSilent) {
        appStore.setNewSnack(10000, 'We could not load your offers. Please try again.', 'error');
      }
    } finally {
      appStore.isFetchOffersInProgress = false;
      if (!isSilent) {
        appStore.isLoaderActive = false;
        appStore.overlayText = '';
      }
    }
  }

  return appStore.account.offers || [];
}
